import { useState, useEffect, useMemo, useCallback } from "react";
import { supabaseAdmin as supabase } from "@/lib/supabaseAdmin";
import {
  getMembershipLabel,
  getMembershipStyle,
  MEMBERSHIP_OPTIONS,
} from "@/lib/membership";
import { useDebounce } from "@/hooks/useDebounce";
import MemberEditPanel from "./MemberEditPanel";

interface Profile {
  id: string;
  name: string | null;
  username: string | null;
  phone: string | null;
  membership_level: string | null;
  balance: number | null;
  total_earnings: number | null;
  withdraw_password: string | null;
  created_at: string | null;
  is_admin: boolean | null;
  account_status: string | null;
  verification_status: string | null;
  bank_name: string | null;
  branch_code: string | null;
  bank_account: string | null;
  account_holder: string | null;
  wallet_balance: number | null;
  pending_profits: number | null;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getStatusLabel(status: string | null): { text: string; style: string } {
  switch (status) {
    case "suspended":
      return { text: "暫停", style: "bg-amber-50 text-amber-600 border-amber-200" };
    case "banned":
      return { text: "封鎖", style: "bg-red-50 text-red-500 border-red-200" };
    default:
      return { text: "啟用", style: "bg-emerald-50 text-emerald-600 border-emerald-200" };
  }
}

function getVerificationLabel(status: string | null): { text: string; style: string } {
  switch (status) {
    case "approved":
      return { text: "已認證", style: "bg-emerald-50 text-emerald-600 border-emerald-200" };
    case "pending":
      return { text: "審核中", style: "bg-amber-50 text-amber-600 border-amber-200" };
    case "rejected":
      return { text: "已拒絕", style: "bg-red-50 text-red-500 border-red-200" };
    default:
      return { text: "未認證", style: "bg-stone-50 text-stone-500 border-stone-200" };
  }
}

export default function AdminUsers() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [newLevel, setNewLevel] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [panelUserId, setPanelUserId] = useState<string | null>(null);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const totalPages = useMemo(() => {
    if (!totalCount || pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [totalCount, pageSize]);

  // Fetch users with pagination & backend search
  const fetchUsers = useCallback(
    async (targetPage: number, targetPageSize: number, targetSearch: string) => {
      setLoading(true);
      setError("");
      try {
        const from = (targetPage - 1) * targetPageSize;
        const to = from + targetPageSize - 1;

        let profilesQuery = supabase
          .from("profiles")
          .select(
            "id, name, username, phone, membership_level, account_status, bank_name, branch_code, bank_account, account_holder, verification_status, is_verified, is_admin, created_at, withdraw_password",
            { count: "exact" }
          )
          .order("created_at", { ascending: false });

        const term = targetSearch.trim();
        if (term) {
          const safeTerm = term.replace(/[%_]/g, "\\$&");
          profilesQuery = profilesQuery.or(
            `name.ilike.%${safeTerm}%,username.ilike.%${safeTerm}%,phone.ilike.%${safeTerm}%`
          );
        }

        profilesQuery = profilesQuery.range(from, to);

        const {
          data: profilesData,
          error: profilesError,
          count,
        } = await profilesQuery;

        if (profilesError) {
          setError(`讀取會員資料失敗：${profilesError.message}`);
          setUsers([]);
          setTotalCount(0);
          return;
        }

        setTotalCount(count || 0);

        // Fetch wallets only for current page users
        const userIds = (profilesData || []).map((p) => p.id);
        let walletsData: Array<{
          user_id: string;
          balance: number | null;
          total_earnings: number | null;
          pending_profits: number | null;
        }> = [];

        if (userIds.length > 0) {
          const { data: wData, error: walletsError } = await supabase
            .from("wallets")
            .select("user_id, balance, total_earnings, pending_profits")
            .in("user_id", userIds);

          if (walletsError) {
            console.error("[AdminUsers] wallets fetch error:", walletsError);
          }
          walletsData = wData || [];
        }

        const walletMap = new Map(
          walletsData.map((w) => [
            w.user_id,
            {
              balance: w.balance ?? 0,
              total_earnings: w.total_earnings ?? 0,
              pending_profits: w.pending_profits ?? 0,
            },
          ])
        );

        const merged = (profilesData || []).map((p) => {
          const wallet = walletMap.get(p.id);
          return {
            ...p,
            wallet_balance: wallet ? wallet.balance : null,
            total_earnings: wallet ? wallet.total_earnings : (p.total_earnings ?? 0),
            pending_profits: wallet ? wallet.pending_profits : 0,
          };
        });

        setUsers(merged);
      } catch (err) {
        setError(err instanceof Error ? err.message : "資料載入失敗");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Initial load + re-fetch on page/pageSize/search changes
  useEffect(() => {
    fetchUsers(page, pageSize, debouncedSearchTerm);
  }, [fetchUsers, page, pageSize, debouncedSearchTerm]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleUpdateLevel = async (userId: string) => {
    if (!newLevel) return;
    setSavingUserId(userId);
    try {
      const { error: updateError } = await supabase.from("profiles").update({ membership_level: newLevel }).eq("id", userId);

      if (updateError) {
        setActionMsg(`更新失敗：${updateError.message}`);
      } else {
        setUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, membership_level: newLevel } : u))
        );
        setActionMsg("等級更新成功");
        setEditingUser(null);
        setNewLevel("");
      }
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : "更新失敗");
    } finally {
      setSavingUserId(null);
      setTimeout(() => setActionMsg(""), 3000);
    }
  };

  // Pagination helpers
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(startIndex + users.length - 1, totalCount);

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, page - 1);
    let end = Math.min(totalPages - 1, page + 1);
    if (page <= 3) {
      start = 2;
      end = Math.min(totalPages - 1, 4);
    }
    if (page >= totalPages - 2) {
      start = Math.max(2, totalPages - 3);
      end = totalPages - 1;
    }
    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("...");
    pages.push(totalPages);
    return pages;
  }, [page, totalPages]);

  return (
    <div className="flex flex-col gap-4">
      {/* Search bar */}
      <div className="flex items-center gap-3 bg-white rounded-xl border border-navy-100 px-4 py-3">
        <div className="w-5 h-5 flex items-center justify-center text-navy-400">
          <i className="ri-search-line text-base" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="搜尋會員姓名、帳號或手機號碼..."
          className="flex-1 font-body text-sm text-navy-900 placeholder-navy-400 outline-none bg-transparent"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer"
          >
            <i className="ri-close-line text-sm" />
          </button>
        )}
      </div>

      {actionMsg && (
        <div
          className={`font-body text-sm px-4 py-2.5 rounded-lg ${
            actionMsg.includes("成功")
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-red-50 text-red-600 border border-red-200"
          }`}
        >
          {actionMsg}
        </div>
      )}

      {error && (
        <div className="font-body text-sm px-4 py-2.5 rounded-lg bg-red-50 text-red-600 border border-red-200">
          {error}
        </div>
      )}

      {debouncedSearchTerm && !loading && (
        <p className="font-body text-xs text-navy-400">
          搜尋「{debouncedSearchTerm}」：共 {totalCount} 位會員
        </p>
      )}

      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-100 bg-navy-50/50">
                {["會員", "帳號", "手機號碼", "會員等級", "帳號狀態", "實名認證", "帳戶餘額", "累計收益", "提領密碼", "加入日期", "操作"].map(
                  (h) => (
                    <th
                      key={h}
                      className="font-body text-[11px] font-semibold text-navy-600 text-left px-4 py-3 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {loading && (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-navy-400">載入中...</p>
                  </td>
                </tr>
              )}

              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-navy-400">
                      {debouncedSearchTerm ? "沒有符合搜尋條件的會員" : "暫無會員資料"}
                    </p>
                  </td>
                </tr>
              )}

              {!loading &&
                users.map((u) => {
                  const isEditing = editingUser === u.id;
                  const level = u.membership_level || "member";
                  const levelLabel = getMembershipLabel(level);
                  const levelStyle = getMembershipStyle(level);
                  const isAdminUser = level === "admin";
                  const name = u.name || "未命名";
                  const status = getStatusLabel(u.account_status);
                  const isSaving = savingUserId === u.id;
                  return (
                    <tr key={u.id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-navy-100 flex items-center justify-center shrink-0">
                            <span className="font-body text-xs font-bold text-navy-600">
                              {name.slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <p className="font-body text-sm font-medium text-navy-900">{name}</p>
                            {isAdminUser && (
                              <span className="font-body text-[10px] font-semibold px-1.5 py-0.5 rounded bg-navy-700 text-white">
                                管理員
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-body text-xs text-navy-500 font-mono">
                        {u.username || "—"}
                      </td>
                      <td className="px-4 py-3 font-body text-xs text-navy-500">
                        {u.phone || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select
                            value={newLevel}
                            onChange={(e) => setNewLevel(e.target.value)}
                            className="font-body text-xs px-2 py-1 rounded-lg border border-navy-200 outline-none"
                          >
                            <option value="">選擇等級</option>
                            {MEMBERSHIP_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${levelStyle}`}
                          >
                            {levelLabel}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${status.style}`}>
                          {status.text}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${getVerificationLabel(u.verification_status).style}`}>
                          {getVerificationLabel(u.verification_status).text}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-body text-sm font-semibold text-navy-900">
                        {u.wallet_balance !== null ? (
                          `NT$ ${u.wallet_balance.toLocaleString()}`
                        ) : (
                          <span className="font-normal text-stone-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-body text-sm text-emerald-600">
                        NT$ {(u.total_earnings ?? 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-body text-xs text-navy-400 font-mono tracking-wider">
                        {u.withdraw_password || "—"}
                      </td>
                      <td className="px-4 py-3 font-body text-xs text-navy-500">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleUpdateLevel(u.id)}
                              disabled={isSaving}
                              className={`font-body text-[10px] font-semibold px-2 py-1 rounded-md text-white cursor-pointer ${
                                isSaving
                                  ? "bg-navy-300 cursor-not-allowed"
                                  : "bg-emerald-500 hover:bg-emerald-600"
                              }`}
                            >
                              {isSaving ? (
                                <span className="flex items-center gap-1">
                                  <i className="ri-loader-4-line animate-spin" />
                                  處理
                                </span>
                              ) : (
                                "儲存"
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setEditingUser(null);
                                setNewLevel("");
                              }}
                              disabled={isSaving}
                              className="font-body text-[10px] px-2 py-1 rounded-md border border-navy-200 text-navy-500 hover:bg-navy-50 cursor-pointer"
                            >
                              取消
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingUser(u.id);
                                setNewLevel(level);
                              }}
                              className="font-body text-[10px] font-semibold px-2 py-1.5 rounded-full border border-navy-200 text-navy-500 hover:border-navy-400 hover:text-navy-700 cursor-pointer"
                            >
                              等級
                            </button>
                            <button
                              onClick={() => setPanelUserId(u.id)}
                              className="font-body text-[10px] font-semibold px-2 py-1.5 rounded-full bg-navy-700 text-white hover:bg-navy-800 cursor-pointer"
                            >
                              編輯
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Pagination bar */}
        {!loading && totalCount > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-navy-100">
            <div className="flex items-center gap-2">
              <span className="font-body text-xs text-navy-500 whitespace-nowrap">
                顯示第 {startIndex}–{endIndex} 筆，共 {totalCount} 筆
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="font-body text-xs px-2 py-1 rounded-md border border-navy-200 outline-none bg-white text-navy-700 cursor-pointer"
              >
                <option value={20}>每頁 20 筆</option>
                <option value={50}>每頁 50 筆</option>
              </select>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-navy-200 text-navy-600 hover:bg-navy-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-sm" />
              </button>

              {pageNumbers.map((pn, idx) =>
                pn === "..." ? (
                  <span
                    key={`dots-${idx}`}
                    className="w-8 h-8 flex items-center justify-center font-body text-xs text-navy-400"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={pn}
                    onClick={() => setPage(Number(pn))}
                    className={`w-8 h-8 flex items-center justify-center rounded-md font-body text-xs font-semibold cursor-pointer ${
                      page === pn
                        ? "bg-navy-700 text-white"
                        : "border border-navy-200 text-navy-600 hover:bg-navy-50"
                    }`}
                  >
                    {pn}
                  </button>
                )
              )}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-navy-200 text-navy-600 hover:bg-navy-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-sm" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Member Edit Panel */}
      {panelUserId && (
        <MemberEditPanel
          userId={panelUserId}
          onClose={() => setPanelUserId(null)}
          onUpdated={() => fetchUsers(page, pageSize, debouncedSearchTerm)}
        />
      )}
    </div>
  );
}